import {noop} from '@benchling/js-utils';
import {createStyles} from '@mantine/core';
import React, {ReactNode, useCallback, useEffect, useRef, useState} from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import ProcedureRunDesignFlowchart, {
  ProcedureRunDesignFlowchartCanvasDnd,
} from 'studies/typed/shared/flowcharts/design/ProcedureRunDesignFlowchart';
import {ExperimentMethod} from 'studies/typed/shared/procedure-runs/design-state/ExperimentalInputs';
import {ConditionGroup} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import getTestProps from 'test/lib/cypress/getTestProps';
import FlowchartContextPanel, {
  PanelSide,
  SidepanelSize,
} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';
import FlowchartSidebar from 'ui-library/reactflow/sidepanels/FlowchartSidebar';
import {useResizableSidepanel} from 'ui-library/reactflow/sidepanels/useResizableSidepanel';

import ProcedureRunMethodSelectDropdown from '../../procedure-runs/procedure-selection/ProcedureRunMethodSelectDropdown';
import {FlowchartEdgeSpec, StatusByNodeId} from './ProcedureRunDesignFlowchartLayoutUtils';
import type {MethodColumnScrollMode} from './useProcedureRunDesignFlowchartAutoScroll';

const LEFT_PANEL_SIZE = SidepanelSize.SMALL;
const RIGHT_PANEL_SIZE = SidepanelSize.MEDIUM;

interface ProcedureRunDesignFlowchartEditorProps {
  conditions: Array<ConditionGroup>;
  methods: Array<ExperimentMethod>;
  activeMethodId: string | null;
  procedureType: ProcedureType;
  experimentalInputs?: ExperimentalInputs;
  selectedMethodId: string | null;
  onChangeSelectedMethod: (methodId: string | null) => void;
  isInEditState: boolean;
  isPlan: boolean;
  showArchived?: boolean;
  /**
   * When false (editing an existing procedure run), persisted conditions render a lock icon in
   * their flowchart nodes.
   */
  allowEditingPersistedConditions: boolean;
  leftPanelChildren: ReactNode;
  rightPanelChildren: ReactNode;
  statusByNodeId?: StatusByNodeId;
  edgeSpecs: Array<FlowchartEdgeSpec>;
  /**
   * Records a newly drawn edge in the draft edge state. Optional: only the run tab is drawable (it
   * supplies an active method); the run plan tab passes `activeMethodId={null}`, so its nodes are never
   * connectable and this never fires.
   */
  onDrawEdge?: (spec: FlowchartEdgeSpec) => void;
  /** Node ids removed in parent-owned draft state. */
  removedNodeIds?: ReadonlySet<string>;
  /** Records removed persisted flowchart node ids in parent-owned draft state. */
  onRemovePersistedConditionReplicate?: (nodeIds: ReadonlySet<string>) => void;
  /**
   * Removes an edge by id. Optional and defaults to a no-op when edge deletion is not available.
   */
  onRemoveEdge?: (edgeId: string) => void;
  /** Removes a draft condition replicate from parent-owned condition state. */
  onRemoveDraftConditionReplicate?: (conditionIndex: number, replicateIndex: number) => void;
  /**
   * Adds a new condition-replicate lane when the flowchart add-replicate button is clicked. Only the
   * split-pool run editor supplies this; other callers omit it and the button never renders.
   */
  onAddConditionReplicate?: () => void;
  /** Disables the add-replicate button (e.g. at the replicate limit); clicks become no-ops. */
  isAddConditionReplicateDisabled?: boolean;
  /** Tooltip shown on the disabled add-replicate button explaining the limit. */
  addConditionReplicateDisabledTooltip?: string;
  /**
   * Optional native HTML5 drag-and-drop wiring for the canvas, forwarded to the flowchart. Used by
   * the procedure-less empty state to attach a procedure dropped onto the empty canvas.
   */
  canvasDnd?: ProcedureRunDesignFlowchartCanvasDnd;
  topBanner?: ReactNode;
}

export default function ProcedureRunDesignFlowchartEditor(
  props: ProcedureRunDesignFlowchartEditorProps
): JSX.Element {
  const {classes, cx} = useStyles();
  const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(false);
  const [selectedLeftSidebarIconIndex, setSelectedLeftSidebarIconIndex] = useState<number | null>(null);
  // Dropdown always centers. Canvas clicks center when obscured. Pane deselect clears pending.
  const [pendingMethodScroll, setPendingMethodScroll] = useState<{
    methodId: string;
    mode: MethodColumnScrollMode;
  } | null>(null);
  // The right ("Design") panel overlays the canvas (absolute positioning), so auto-scroll needs its
  // width to center a selected method column in the still-visible canvas rather than behind it.
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const {onChangeSelectedMethod} = props;
  const handleMethodScrollConsumed = useCallback(() => {
    setPendingMethodScroll(null);
  }, []);
  const handleDropdownMethodChange = useCallback(
    (methodId: string) => {
      setPendingMethodScroll({methodId, mode: 'always'});
      onChangeSelectedMethod(methodId);
    },
    [onChangeSelectedMethod]
  );
  const handleCanvasMethodChange = useCallback(
    (methodId: string | null) => {
      if (methodId == null) {
        setPendingMethodScroll(null);
        onChangeSelectedMethod(null);
        return;
      }
      setPendingMethodScroll({methodId, mode: 'whenObscured'});
      onChangeSelectedMethod(methodId);
    },
    [onChangeSelectedMethod]
  );
  const handleSelectedLeftSidebarIconIndexChange = useCallback((index: number | null) => {
    setSelectedLeftSidebarIconIndex(index);
    setIsLeftPanelOpen(index != null);
  }, []);
  const {
    width: resizedWidth,
    isResizing,
    handleMouseDown: handleResizeMouseDown,
    containerRef,
    onPanelSizeChange,
  } = useResizableSidepanel({
    storageKey: 'studies-procedure-design-right-sidepanel-width',
    defaultPanelSize: RIGHT_PANEL_SIZE,
  });

  // Open left sidepanel when user enters edit mode
  useEffect(() => {
    if (!props.isInEditState) {
      return;
    }

    setIsLeftPanelOpen(true);
    setSelectedLeftSidebarIconIndex(0);
  }, [props.isInEditState]);

  return (
    <div
      ref={containerRef}
      className={classes.root}
      {...getTestProps({component: 'ProcedureRunDesignFlowchartEditor', element: 'root'})}
    >
      <FlowchartSidebar
        icons={[
          {
            name: 'create',
            onClick: () => {
              setIsLeftPanelOpen(true);
            },
          },
        ]}
        selectedIconIndex={selectedLeftSidebarIconIndex}
        setSelectedIconIndex={handleSelectedLeftSidebarIconIndexChange}
      />
      <FlowchartContextPanel
        panelSide={PanelSide.LEFT}
        className={classes.leftPanel}
        sidepanelName={props.isPlan ? 'Run Plan' : 'Run Design'}
        isOpen={isLeftPanelOpen}
        onClose={() => {
          setIsLeftPanelOpen(false);
          setSelectedLeftSidebarIconIndex(null);
        }}
        size={LEFT_PANEL_SIZE}
        showExpandCollapseButtons={false}
      >
        {props.leftPanelChildren}
      </FlowchartContextPanel>
      <div className={cx(classes.flowchartContainer)}>
        <ProcedureRunDesignFlowchart
          conditions={props.conditions}
          methods={props.methods}
          activeMethodId={props.activeMethodId}
          procedureType={props.procedureType}
          experimentalInputs={props.experimentalInputs}
          topBanner={props.topBanner}
          controlsShiftLeft={0}
          statusByNodeId={props.statusByNodeId}
          selectedMethodId={props.selectedMethodId}
          setSelectedMethodId={handleCanvasMethodChange}
          pendingMethodIdToScroll={pendingMethodScroll?.methodId ?? null}
          pendingMethodScrollMode={pendingMethodScroll?.mode}
          onMethodScrollConsumed={handleMethodScrollConsumed}
          rightSidepanelRef={rightPanelRef}
          edgeSpecs={props.edgeSpecs}
          onDrawEdge={props.onDrawEdge ?? noop}
          onRemoveEdge={props.onRemoveEdge ?? noop}
          onAddConditionReplicate={props.onAddConditionReplicate}
          isAddConditionReplicateDisabled={props.isAddConditionReplicateDisabled}
          addConditionReplicateDisabledTooltip={props.addConditionReplicateDisabledTooltip}
          onRemoveDraftConditionReplicate={props.onRemoveDraftConditionReplicate}
          removedNodeIds={props.removedNodeIds}
          onRemovePersistedConditionReplicate={props.onRemovePersistedConditionReplicate}
          allowEditingPersistedConditions={props.allowEditingPersistedConditions}
          isEditable={props.isInEditState}
          showArchived={props.showArchived}
          canvasDnd={props.canvasDnd}
        />
      </div>
      <FlowchartContextPanel
        panelSide={PanelSide.RIGHT}
        outerRef={rightPanelRef}
        sidepanelName="Design"
        headerContent={
          props.selectedMethodId != null ? (
            <ProcedureRunMethodSelectDropdown
              methods={props.methods}
              selectedMethodId={props.selectedMethodId}
              onChangeSelectedMethod={handleDropdownMethodChange}
            />
          ) : undefined
        }
        // The right ("Design") panel is open exactly when a method is selected. Closing it clears the
        // selection (matching the Workflows flowchart editor); selecting a column reopens it.
        isOpen={props.selectedMethodId != null}
        onClose={() => {
          handleCanvasMethodChange(null);
        }}
        size={RIGHT_PANEL_SIZE}
        showExpandCollapseButtons={true}
        resizable={true}
        resizedWidth={resizedWidth}
        isResizing={isResizing}
        onResizeMouseDown={handleResizeMouseDown}
        onPanelSizeChange={onPanelSizeChange}
      >
        {props.rightPanelChildren}
      </FlowchartContextPanel>
    </div>
  );
}

const useStyles = createStyles(() => ({
  root: {
    display: 'flex',
    position: 'relative',
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    width: '100%',
  },
  flowchartContainer: {
    flex: 1,
    position: 'relative',
    minWidth: 0,
  },
  leftPanel: {
    minHeight: 0,
  },
}));
